import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  addEdge,
  Background,
  BackgroundVariant,
  Controls,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { AlertTriangle, Bot, Download, Headset, LayoutIcon, Upload } from 'lucide-react';

import { Button } from '@/components/ui/button';

import { TOOL_VALUES, conversationFlowSchema } from '@repo/shared-types';

import {
  agentGraphToFlow,
  autoLayoutAgentGraph,
  createDefaultAiAgent,
  createDefaultHumanAgent,
  flowToAgentGraph,
} from './agent-graph-utils';
import { AgentConfigPanel } from './AgentConfigPanel';
import { AiAgentNode } from './nodes/AiAgentNode';
import { HumanNode } from './nodes/HumanNode';

import type { CustomToolOption } from './AgentConfigPanel';
import type { AgentGraph, AgentNode, TransferTarget } from '@repo/shared-types';
import type { Connection, Edge, EdgeMouseHandler, Node, NodeMouseHandler } from '@xyflow/react';

const nodeTypes = {
  aiAgent: AiAgentNode,
  humanAgent: HumanNode,
};

interface AgentGraphCanvasProps {
  value: AgentGraph | null;
  onChange: (graph: AgentGraph) => void;
  transferTargets: TransferTarget[];
  customTools?: CustomToolOption[];
}

function AgentGraphCanvasInner({
  value,
  onChange,
  transferTargets,
  customTools,
}: AgentGraphCanvasProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const flowPolicyRef = useRef(value?.conversation_flow);
  useEffect(() => {
    flowPolicyRef.current = value?.conversation_flow;
  }, [value?.conversation_flow]);
  const [importError, setImportError] = useState('');
  const [initial] = useState(() => {
    if (!value) return { nodes: [] as Node[], edges: [] as Edge[] };
    return agentGraphToFlow(value);
  });

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>(initial.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(initial.edges);

  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<Edge | null>(null);

  // Track whether a change originated internally to avoid circular syncs
  const internalChangeRef = useRef(false);

  // Sync external value changes into state (e.g. parent resets graph)
  const prevValueRef = useRef(value);
  useEffect(() => {
    if (internalChangeRef.current) {
      internalChangeRef.current = false;
      return;
    }
    if (value === prevValueRef.current) return;
    prevValueRef.current = value;
    if (!value) {
      setNodes([]);
      setEdges([]);
      return;
    }
    const { nodes: n, edges: e } = agentGraphToFlow(value);
    setNodes(n);
    setEdges(e);
  }, [value, setNodes, setEdges]);

  // Sync to parent — only on data/edge changes, not position drags.
  // Position updates sync on drag end via onNodeDragStop.
  const syncTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const syncToParent = useCallback(() => {
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      internalChangeRef.current = true;
      const graph = flowToAgentGraph(nodes, edges, flowPolicyRef.current);
      prevValueRef.current = graph;
      onChange(graph);
    }, 300);
  }, [nodes, edges, onChange]);

  // Sync when edges change (add/remove/edit)
  const edgeLengthRef = useRef(edges.length);
  useEffect(() => {
    if (edgeLengthRef.current !== edges.length) {
      edgeLengthRef.current = edges.length;
      syncToParent();
    }
  }, [edges.length, syncToParent]);

  // Agents list derived from nodes for the panel
  const agents = useMemo(() => nodes.map((n) => n.data as AgentNode), [nodes]);

  const hasEntryAgent = useMemo(() => agents.some((a) => a.is_entry), [agents]);

  // --- Handlers ---

  const onNodeClick: NodeMouseHandler = useCallback((_, node) => {
    setSelectedNode(node);
    setSelectedEdge(null);
  }, []);

  const onEdgeClick: EdgeMouseHandler = useCallback((_, edge) => {
    setSelectedEdge(edge);
    setSelectedNode(null);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
  }, []);

  const onNodeDragStop = useCallback(() => {
    syncToParent();
  }, [syncToParent]);

  const onConnect = useCallback(
    (connection: Connection) => {
      // Prevent self-loops
      if (connection.source === connection.target) return;

      // Prevent duplicate edges
      const exists = edges.some(
        (e) => e.source === connection.source && e.target === connection.target,
      );
      if (exists) return;

      setEdges((eds) =>
        addEdge(
          {
            ...connection,
            id: `edge_${Date.now()}`,
            type: 'default',
            animated: true,
            style: { strokeWidth: 2, stroke: '#6366f1' },
            data: { description: '' },
          },
          eds,
        ),
      );
    },
    [edges, setEdges],
  );

  const handleAddAiAgent = useCallback(() => {
    const offset = nodes.length * 60;
    const agent = createDefaultAiAgent({ x: 250 + offset, y: 100 + offset });
    const newNode: Node = {
      id: agent.id,
      type: 'aiAgent',
      position: agent.position,
      data: { ...agent },
    };
    setNodes((nds) => [...nds, newNode]);
  }, [nodes.length, setNodes]);

  const handleAddHumanAgent = useCallback(() => {
    const offset = nodes.length * 60;
    const agent = createDefaultHumanAgent({ x: 250 + offset, y: 200 + offset });
    const newNode: Node = {
      id: agent.id,
      type: 'humanAgent',
      position: agent.position,
      data: { ...agent },
    };
    setNodes((nds) => [...nds, newNode]);
  }, [nodes.length, setNodes]);

  const handleAutoLayout = useCallback(() => {
    const laid = autoLayoutAgentGraph(nodes, edges);
    setNodes(laid);
  }, [nodes, edges, setNodes]);

  const handleSetEntry = useCallback(
    (nodeId: string) => {
      setNodes((nds) =>
        nds.map((n) => ({
          ...n,
          data: {
            ...(n.data as AgentNode),
            is_entry: n.id === nodeId,
          },
        })),
      );
      // Update the selected node too
      setSelectedNode((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          data: {
            ...(prev.data as AgentNode),
            is_entry: prev.id === nodeId,
          },
        };
      });
    },
    [setNodes],
  );

  const handleNodeDataChange = useCallback(
    (nodeId: string, data: AgentNode) => {
      setNodes((nds) => nds.map((n) => (n.id === nodeId ? { ...n, data: { ...data } } : n)));
      setSelectedNode((prev) => (prev?.id === nodeId ? { ...prev, data: { ...data } } : prev));
      syncToParent();
    },
    [setNodes, syncToParent],
  );

  const handleEdgeDataChange = useCallback(
    (edgeId: string, data: { description: string; requires_tools?: string[] }) => {
      setEdges((eds) =>
        eds.map((e) => (e.id === edgeId ? { ...e, data: { ...e.data, ...data } } : e)),
      );
      setSelectedEdge((prev) =>
        prev?.id === edgeId ? { ...prev, data: { ...prev.data, ...data } } : prev,
      );
      syncToParent();
    },
    [setEdges, syncToParent],
  );

  const handleDeleteNode = useCallback(
    (nodeId: string) => {
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
      setSelectedNode(null);
      syncToParent();
    },
    [setNodes, setEdges, syncToParent],
  );

  const handleDeleteEdge = useCallback(
    (edgeId: string) => {
      setEdges((eds) => eds.filter((e) => e.id !== edgeId));
      setSelectedEdge(null);
    },
    [setEdges],
  );

  const handleImport = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const json = JSON.parse(e.target?.result as string);
          if (!json.agents || !Array.isArray(json.agents)) return;

          const agents: AgentNode[] = json.agents.map((a: Record<string, unknown>, i: number) => ({
            ...a,
            id: (a.id as string) || crypto.randomUUID(),
            position: (a.position as { x: number; y: number }) || { x: i * 250, y: 0 },
            tools: Array.isArray(a.tools)
              ? (a.tools as string[]).filter((t) => (TOOL_VALUES as readonly string[]).includes(t))
              : [],
            custom_tool_ids: a.custom_tool_ids || [],
            is_entry: a.is_entry ?? false,
          }));

          const agentsByName = new Map(agents.map((a) => [a.name, a]));

          const rawEdges: Record<string, unknown>[] = Array.isArray(json.edges) ? json.edges : [];
          const flowEdges: Edge[] = rawEdges.reduce(
            (acc: Edge[], edge: Record<string, unknown>, i: number) => {
              const src = agentsByName.get(edge.source as string);
              const tgt = agentsByName.get(edge.target as string);
              if (!src || !tgt) return acc;
              const rt = Array.isArray(edge.requires_tools)
                ? (edge.requires_tools as string[]).filter((s) => typeof s === 'string' && s)
                : [];
              acc.push({
                id: (edge.id as string) || `edge_import_${i}`,
                source: src.id,
                target: tgt.id,
                type: 'smoothstep',
                style: { strokeWidth: 2 },
                data: {
                  description: (edge.description as string) || '',
                  ...(rt.length ? { requires_tools: rt } : {}),
                },
              });
              return acc;
            },
            [],
          );

          const flowNodes: Node[] = agents.map((a) => ({
            id: a.id,
            type: a.type === 'human' ? 'humanAgent' : 'aiAgent',
            position: a.position,
            data: { ...a },
          }));

          const laid = autoLayoutAgentGraph(flowNodes, flowEdges);
          const policy = json.conversation_flow
            ? conversationFlowSchema.parse(json.conversation_flow)
            : undefined;
          if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
          flowPolicyRef.current = policy;
          setNodes(laid);
          setEdges(flowEdges);
          onChange(flowToAgentGraph(laid, flowEdges, policy));
          setImportError('');
        } catch (err) {
          console.error('Failed to import agent graph:', err);
          setImportError(
            'Graph import failed. Check the JSON and conversation policy; your existing graph is unchanged.',
          );
        }
      };
      reader.readAsText(file);
      event.target.value = '';
    },
    [setNodes, setEdges, onChange],
  );

  const handleExport = useCallback(() => {
    const graph = flowToAgentGraph(nodes, edges, flowPolicyRef.current);
    const blob = new Blob([JSON.stringify(graph, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'agent-graph.json';
    a.click();
    URL.revokeObjectURL(url);
  }, [nodes, edges]);

  return (
    <div className="relative flex h-full min-h-[500px] overflow-hidden rounded-lg border">
      {importError && (
        <p
          role="alert"
          className="bg-background text-destructive absolute bottom-3 left-3 z-20 max-w-md rounded border p-3 text-sm"
        >
          {importError}
        </p>
      )}
      <div className="relative flex-1">
        {/* Toolbar */}
        <div className="absolute top-2 left-2 z-10 flex gap-1.5">
          <Button type="button" size="sm" variant="outline" onClick={handleAddAiAgent}>
            <Bot size={14} className="mr-1" />
            Add AI Agent
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={handleAddHumanAgent}>
            <Headset size={14} className="mr-1" />
            Add Human Target
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={handleAutoLayout}>
            <LayoutIcon size={14} className="mr-1" />
            Auto Layout
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload size={14} className="mr-1" />
            Import
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={handleExport}>
            <Download size={14} className="mr-1" />
            Export
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            className="hidden"
            onChange={handleImport}
          />
        </div>

        {/* Validation warning */}
        {!hasEntryAgent && nodes.length > 0 && (
          <div className="absolute top-2 right-2 z-10 flex items-center gap-1.5 rounded-md border border-yellow-300 bg-yellow-50 px-3 py-1.5 text-xs text-yellow-800 dark:border-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-300">
            <AlertTriangle size={14} />
            No entry agent set. Select an agent and click &quot;Set as Entry Agent&quot;.
          </div>
        )}

        {/* React Flow */}
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onEdgeClick={onEdgeClick}
          onPaneClick={onPaneClick}
          onNodeDragStop={onNodeDragStop}
          nodeTypes={nodeTypes}
          defaultEdgeOptions={{
            type: 'default',
            animated: true,
            style: { strokeWidth: 2, stroke: '#6366f1' },
          }}
          fitView
          fitViewOptions={{ padding: 0.3 }}
          proOptions={{ hideAttribution: true }}
          deleteKeyCode={['Backspace', 'Delete']}
        >
          <Background variant={BackgroundVariant.Dots} gap={24} size={1} />
          <Controls />
        </ReactFlow>
      </div>

      {/* Side panel — only visible when something is selected */}
      {(selectedNode || selectedEdge) && (
        <div className="bg-background absolute top-0 right-0 bottom-0 w-[380px] overflow-y-auto border-l [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <AgentConfigPanel
            selectedNode={selectedNode}
            selectedEdge={selectedEdge}
            onNodeDataChange={handleNodeDataChange}
            onEdgeDataChange={handleEdgeDataChange}
            onDeleteNode={handleDeleteNode}
            onDeleteEdge={handleDeleteEdge}
            onSetEntry={handleSetEntry}
            transferTargets={transferTargets}
            agents={agents}
            customTools={customTools}
          />
        </div>
      )}
    </div>
  );
}

export const AgentGraphCanvas = (props: AgentGraphCanvasProps) => (
  <ReactFlowProvider>
    <AgentGraphCanvasInner {...props} />
  </ReactFlowProvider>
);

export type { AgentGraphCanvasProps };
